import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import DataTable from "../../data-table/data-table";
import Topbar from "../../top-bar/top-bar";
import {instanceMob} from "../../../utils/utils";
import useLocationStore from "../config/use-location-store";
import FormField from "../../forms/form-field";
import debounce from "lodash.debounce";
import DateRangePicker from "../../date-range-picker/date-range-picker";
import Tooltip from "../../tooltip/tooltip";
import "./styles.css";

const IncomesAndExpensesTable = () => {
    const navigate = useNavigate();
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useRef(null);
    const [showDateModal, setShowDateModal] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [sortConfig, setSortConfig] = useState({ accessor: null, direction: null });

    const handleOpenDateModal = () => {
        setShowDateModal(true);
        document.activeElement?.blur();
    };
    const handleCloseDateModal = () => setShowDateModal(false);

    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null,
        currentPage: 1,
    });

    const columns = [
        {
            header: "Fecha",
            accessor: "date",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Registro",
            accessor: "registry",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Tipo",
            accessor: "type",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Responsable",
            accessor: "seller",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Producto",
            renderCell: (row) => (
                <div className="product-list gap-3 text-truncate">
                    {row.products?.map((p, i) => (
                        <div key={i} className="text-truncate p-0">{p.name}</div>
                    ))}
                </div>
            ),
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Tercero",
            accessor: "buyer",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Cantidad",
            renderCell: (row) => (
                <div className="product-list gap-3">
                    {row.products?.map((p, i) => (
                        <div key={i} className="text-truncate p-0">{p.quantity}</div>
                    ))}
                </div>
            ),
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Precio",
            renderCell: (row) => (
                <div className="product-list gap-3">
                    {row.products?.map((p, i) => (
                        <div key={i} className="text-truncate p-0">{p.price}</div>
                    ))}
                </div>
            ),
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Total",
            accessor: "total",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            },
            sortable: true,
        },
        {
            header: "Medio de pago",
            accessor: "paymentMethod",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
        {
            header: "Descripción Gasto",
            accessor: "description",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-items-start"
            }
        },
    ];

    const handleNext = () => {
        if (pagination.next) {
            setPagination((prev) => ({
                ...prev,
                currentPage: prev.currentPage + 1,
            }));
        }
    };

    const handlePrevious = () => {
        if (pagination.previous && pagination.currentPage > 1) {
            setPagination((prev) => ({
                ...prev,
                currentPage: prev.currentPage - 1,
            }));
        }
    };

    const handleApplyDateRange = (range) => {
        setSelectedDateRange(range);
        setShowDateModal(false);
    };

    useEffect(() => {
        if (!selectedLocation?.id) return;

        if (debouncedSearch.current) {
            debouncedSearch.current.cancel();
        }

        debouncedSearch.current = debounce(() => {
            setIsLoading(true);

            let ordering;
            if (sortConfig.accessor && sortConfig.direction) {
                const prefix = sortConfig.direction === "desc" ? "-" : "";
                ordering = `${prefix}${sortConfig.accessor}`;
            }

            instanceMob.get(`/admin/accounting/movements/list`, {
                params: {
                    location_id: selectedLocation.id,
                    page: pagination.currentPage,
                    search: searchTerm || undefined,
                    start_date: selectedDateRange?.start || undefined,
                    end_date: selectedDateRange?.end || undefined,
                    ordering
                },
            })
                .then((res) => {
                    const data = res.data.results;

                    const mapped = data.map((item) => {
                        const products = item.products
                            ? item.products.map((p) => ({
                                ...p,
                                price: p.price
                                    ? Number(p.price).toLocaleString("es-CO", {
                                        style: "currency",
                                        currency: "COP",
                                    })
                                    : "N/A",
                            }))
                            : [
                                {
                                    name: item.product ?? "N/A",
                                    quantity: item.quantity ?? "N/A",
                                    price: item.price
                                        ? Number(item.price).toLocaleString("es-CO", {
                                            style: "currency",
                                            currency: "COP",
                                        })
                                        : "N/A",
                                },
                            ];

                        return {
                            id: item.registry_number,
                            date: item.date,
                            registry: item.registry_number,
                            type: item.type,
                            seller: item.responsible,
                            buyer: item.customer || "N/A",
                            paymentMethod: item.payment_method,
                            total: item.total
                                ? Number(item.total).toLocaleString("es-CO", {
                                    style: "currency",
                                    currency: "COP",
                                })
                                : "N/A",
                            products,
                            description: item?.description || "N/A",
                        };
                    });

                    setTableData(mapped);
                    setPagination((prev) => ({
                        ...prev,
                        count: res.data.count,
                        next: res.data.next,
                        previous: res.data.previous,
                    }));
                })
                .catch((err) => {
                    console.error("Error loading movements:", err);
                })
                .finally(() => setIsLoading(false));
        }, 500);

        debouncedSearch.current();

        return () => {
            if (debouncedSearch.current) debouncedSearch.current.cancel();
        };
    }, [
        selectedLocation?.id,
        pagination.currentPage,
        searchTerm,
        selectedDateRange?.start,
        selectedDateRange?.end,
        sortConfig
    ]);

    const handleDownloadExcel = async () => {
        if (!selectedLocation?.id) return;

        setIsDownloading(true);
        try {
            const response = await instanceMob.get(
                `/admin/accounting/movements/excel`,
                {
                    params: {location_id: selectedLocation.id},
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `incomes_expenses_location_${selectedLocation.id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar Excel:", error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="w-100">
            <Topbar/>
            <div className="incomes-expenses-page w-100 p-3 px-3 gap-4 d-flex flex-column">
                <div className="d-flex flex-row justify-content-between align-items-center position-relative">
                    <div className="d-flex flex-row gap-4 align-items-center">
                        <div className="d-flex flex-row gap-1 justify-content-center align-items-center">
                            <button
                                type="button"
                                className="btn close-button p-1"
                                onClick={() => navigate(-1)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="36"
                                    height="36"
                                    viewBox="0 0 36 36"
                                    fill="none"
                                >
                                    <path
                                        d="M25.5811 34.4648H9.88379C4.97749 34.4648 1 30.4874 1 25.5811V9.88379C1 4.97749 4.97749 1 9.88379 1H25.5811C30.4874 1 34.4648 4.97749 34.4648 9.88379V25.5811L34.4531 26.0381C34.223 30.5807 30.5807 34.223 26.0381 34.4531L25.5811 34.4648Z"
                                        stroke="#9DACBD"
                                        strokeWidth="2"
                                    />
                                    <path
                                        d="M11.0018 17.6351C11.0018 17.0388 11.2292 16.4425 11.6842 15.9875L20.7139 6.95782C21.6238 6.04792 23.0991 6.04792 24.009 6.95782C24.9189 7.86772 24.9189 9.34307 24.009 10.253L16.6268 17.6351L24.009 25.0172C24.9189 25.9271 24.9189 27.4024 24.009 28.3124C23.0991 29.2223 21.6238 29.2223 20.7139 28.3124L11.6842 19.2827C11.2292 18.8277 11.0018 18.2313 11.0018 17.6351Z"
                                        fill="#9DACBD"
                                    />
                                </svg>
                            </button>
                            <div className="close-button-label">Volver</div>
                        </div>

                        <FormField
                            name="search"
                            type="search"
                            placeholder="Buscar"
                            width="269px"
                            isLoading={isLoading}
                            showLabel={false}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoComplete="off"
                        />
                    </div>

                    <div className="inventory-page-title position-absolute top-50 start-50 translate-middle">
                        Ingresos y Egresos
                    </div>

                    <div className="d-flex flex-row gap-2 align-items-center">
                        <Tooltip
                            title={
                                selectedDateRange?.start && selectedDateRange?.end
                                    ? `${selectedDateRange.start} - ${selectedDateRange.end}`
                                    : undefined
                            }
                            position={"bottom"}
                        >
                            <button
                                type="button"
                                className={`btn border-button text-truncate ${
                                    selectedDateRange?.start && selectedDateRange?.end ? "has-date" : ""
                                }`}
                                onClick={handleOpenDateModal}
                            >
                                Fecha
                            </button>
                        </Tooltip>


                        <button
                            type="button"
                            className="btn download-button"
                            disabled={isDownloading}
                            onClick={handleDownloadExcel}
                        >
                            Descargar Datos
                        </button>
                    </div>

                </div>
                <DataTable
                    columns={columns}
                    data={isLoading ? [] : tableData}
                    isLoading={isLoading}
                    variant="compact"
                    pagination={{
                        count: pagination.count,
                        next: pagination.next,
                        previous: pagination.previous,
                        currentPage: pagination.currentPage,
                        onNext: handleNext,
                        onPrevious: handlePrevious,
                    }}
                    onSort={({ accessor, direction }) => setSortConfig({ accessor, direction })}
                />
                <DateRangePicker
                    show={showDateModal}
                    handleClose={handleCloseDateModal}
                    onApply={handleApplyDateRange}
                    currentRange={selectedDateRange}
                />
            </div>
        </div>

    );
};

export default IncomesAndExpensesTable;